import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import AuthLayout from '../../../layouts/AuthLayout/AuthLayout';
import { useLanguage } from '../../../context/LanguageProvider/LanguageProvider';
import { useAuth } from '../../../context/AuthProvider/AuthProvider';

import { NioBrand, NioCard, NioToaster } from '../../../components';
import AuthLogo from '../../../components/AuthLogo/AuthLogo';
import NioField from '../../../components/NioField/NioField';
import { apiLogin } from '../../../api/auth';

const POST_AUTH_REDIRECT = '/';

export default function Login() {
  const { t } = useLanguage();
  const { login, isAuthenticated, ready } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (ready && isAuthenticated) {
      navigate(POST_AUTH_REDIRECT, { replace: true });
    }
  }, [ready, isAuthenticated, navigate]);
  const [signupNotice, setSignupNotice] = useState(null);
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    const msg = location.state?.oauthErrorMsg;
    if (typeof msg === 'string' && msg.trim()) {
      setError('root', { type: 'manual', message: msg.trim() });
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    if (location.state?.signupSuccess) {
      setSignupNotice('success');
      if (location.state.email) {
        setValue('email', String(location.state.email));
      }
      navigate(location.pathname, { replace: true, state: {} });
      const timer = setTimeout(() => setSignupNotice(null), 6000);
      return () => clearTimeout(timer);
    }
    if (location.state?.signupEmailTaken) {
      setSignupNotice('taken');
      if (location.state.email) {
        setValue('email', String(location.state.email));
      }
      navigate(location.pathname, { replace: true, state: {} });
      const timer = setTimeout(() => setSignupNotice(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, location.state, navigate, setError, setValue]);

  async function onSubmit(data) {
    clearErrors('root');
    try {
      const res = await apiLogin({ email: data.email, password: data.password });
      login({ token: res.token, user: res.user });
      navigate(POST_AUTH_REDIRECT, { replace: true });
    } catch {
      setError('root', { type: 'manual', message: t('auth.login.err.invalidCredentials') });
    }
  }
  return (
    <AuthLayout titleKey="auth.login.docTitle" rootClass="layout-1">
      {signupNotice === 'success' && (
        <NioToaster
          variant="success"
          messages={[
            {
              id: 1,
              header: t('auth.login.signupSuccessTitle'),
              body: t('auth.login.signupSuccessBody'),
            },
          ]}
        />
      )}
      {signupNotice === 'taken' && (
        <NioToaster
          variant="info"
          messages={[
            {
              id: 2,
              header: t('auth.login.signupEmailTakenTitle'),
              body: t('auth.login.signupEmailTakenBody'),
            },
          ]}
        />
      )}
      <div className="jassp-auth-panel px-2">
        <NioCard className="jassp-auth-card border-0 shadow-none">
          <NioCard.Body className="p-0">
            <div className="jassp-auth-logo mb-4">
              <AuthLogo />
            </div>

            <h1 className="jassp-auth-title">{t('auth.login.title')}</h1>

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              {errors.root?.message ? (
                <p className="text-danger small mb-3" role="alert">
                  {errors.root.message}
                </p>
              ) : null}
              <Row className="gy-3">
                <Col xs={12}>
                  <NioField className="jassp-auth-field" htmlFor="email" label={t('auth.login.emailLabel')}>
                    <NioField.Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder=""
                      {...register('email', {
                        required: t('auth.login.err.required'),
                        pattern: {
                          value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                          message: t('auth.login.err.invalidEmail'),
                        },
                      })}
                    />
                  </NioField>
                </Col>
                <Col xs={12}>
                  <NioField className="jassp-auth-field" htmlFor="password" label={t('auth.login.passwordLabel')}>
                    <NioField.Input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      icon="eye-fill after text-primary"
                      placeholder=""
                      {...register('password', {
                        required: t('auth.login.err.required'),
                      })}
                    />
                  </NioField>
                </Col>
              </Row>

              <p className="jassp-auth-recaptcha-note mb-0">
                {t('auth.login.recaptcha')}{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                  {t('auth.login.recaptchaPrivacy')}
                </a>{' '}
                {t('auth.login.recaptchaAnd')}{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                  {t('auth.login.recaptchaTerms')}
                </a>{' '}
                {t('auth.login.recaptchaApply')}
              </p>

              <button type="submit" className="jassp-auth-submit mt-4">
                {t('auth.login.submit')}
              </button>
            </form>

            <div className="text-center mt-4">
              <Link to="/auth/forgot-password" className="jassp-auth-link fs-15">
                {t('auth.login.forgot')}
              </Link>
            </div>

            <p className="text-center mt-4 mb-0 small text-secondary">
              {t('auth.login.noAccount')}{' '}
              <Link to="/auth/sign-up" className="jassp-auth-link fw-semibold">
                {t('auth.login.signupLink')}
              </Link>
            </p>
          </NioCard.Body>
        </NioCard>
      </div>
    </AuthLayout>
  );
}
